import React from 'react'

import { useMutation } from '@apollo/client'

import ActionButton, {
  ActionButtonIcon,
  ActionButtonType,
} from '$extensionSrc/components/ActionButton'
import Toast from '$extensionSrc/components/Toast'
import {
  appleMailURL,
  gmailURL,
  loginPath,
  sendResetPassword,
} from '$extensionSrc/consts/urlConsts'
import useForm from '$extensionSrc/hooks/useForm'
import { isValidEmail } from '$extensionSrc/utils/stringValidator'

import FormSubmitButton from './FormSubmitButton'
import FormTextDescription from './FormTextDescription'
import FormTextInput from './FormTextInput'
import { unauthClient } from '../apolloClientUnauth'
import { sendResetPasswordEmailMutation } from '../data/queryUnauth'

import './SendPasswordResetForm.scss'

export default function SendPasswordResetForm() {
  const defaultValues = { email: '' }
  const requiredValidations = {
    email: (x) => isValidEmail(x),
  }

  const {
    getFieldChangeHandler,
    getRequiredValidationMetByField,
    isValid,
    values,
  } = useForm({
    defaultValues,
    requiredValidations,
  })

  const [submitResetRequest, { data: resetResponseData }] = useMutation(
    sendResetPasswordEmailMutation,
    { client: unauthClient },
  )

  const resetPwStatusCode =
    resetResponseData?.requestPasswordResetEmail?.statusCode
  const resetPwError = resetResponseData?.requestPasswordResetEmail?.error

  const submitForm = (event) => {
    event.preventDefault()
    submitResetRequest({
      variables: {
        email: values.email,
      },
    })
  }

  return (
    <form className="SendPasswordResetForm" onSubmit={submitForm}>
      {resetPwStatusCode === 200 && (
        <>
          <div className="form-spacer" />
          <div className="header">
            <div className="header-content heading1 bold">
              Check your email for a reset link
            </div>
            <div className="header-content body1">
              If an account exists, you'll get a link at {values.email} to reset
              your password.
            </div>
          </div>
          <div className="body">
            <ActionButton
              className="email-button apple"
              iconName={ActionButtonIcon.APPLE_MAIL}
              iconBorder
              onClick={() => window.open(appleMailURL, '_blank')}
              type={ActionButtonType.SECONDARY}
            >
              Open Mail
            </ActionButton>
            <ActionButton
              className="email-button"
              iconName={ActionButtonIcon.GMAIL}
              iconBorder
              onClick={() => window.open(gmailURL, '_blank')}
              type={ActionButtonType.SECONDARY}
            >
              Open Gmail
            </ActionButton>
          </div>
          <FormTextDescription align="center">
            Didn't get a link? Check your spam folder first.
            <br />
            If you still don't see it,{' '}
            <a href={sendResetPassword}>get a new link</a>.
          </FormTextDescription>
        </>
      )}
      {resetPwStatusCode !== 200 && (
        <>
          <div className="form-spacer" />
          <div className="form-header">
            <div className="form-title bold heading1">
              Enter your account email
            </div>
            <div className="form-subtitle body1">
              This is where we'll send your password reset link.
            </div>
          </div>
          <div className="form-content">
            <FormTextInput
              placeholderText="Email address"
              name="email"
              isSensitive={false}
              value={values.email}
              handleChange={(value) => getFieldChangeHandler('email')(value)}
              requirements={[
                {
                  text: 'Enter a valid email address',
                  isFulfilled: getRequiredValidationMetByField('email'),
                },
              ]}
            />
          </div>
          <div className="form-buttons">
            <FormSubmitButton isValid={isValid}>
              Get a reset link
            </FormSubmitButton>
            <ActionButton
              className="email-button"
              onClick={() => window.location.assign(loginPath)}
              type={ActionButtonType.TERTIARY}
            >
              Cancel
            </ActionButton>
          </div>
        </>
      )}
      {resetPwStatusCode !== 200 && resetPwError && (
        <Toast text="Something went wrong. Try again later." color="red" />
      )}
    </form>
  )
}
